import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackLead } from "../lib/analytics";
import { Headset } from "lucide-react";

const TIER_META = {
  silver: { label: "Silver Support", subtitle: "Startups · 1–20 staff", accent: "text-slate-600 bg-slate-100 border-slate-300" },
  gold: { label: "Gold Support", subtitle: "Small–medium businesses · 1–100 staff", accent: "text-amber-700 bg-amber-100 border-amber-300" },
  diamond: { label: "Diamond Support", subtitle: "Dedicated on-site IT expert", accent: "text-indigo-700 bg-indigo-100 border-indigo-300" },
};

/**
 * Open/close controlled dialog for Managed Services tier enquiry.
 * Fields: Name, Phone, Email (required) + Company, # of users, Tier selector.
 */
export default function ManagedTierEnquiryDialog({ open, onOpenChange, initialTier = "silver" }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", num_users: 10, tier: initialTier, message: "",
  });

  // When the user re-opens the dialog with a different tier, refresh selection.
  useEffect(() => {
    if (open) setForm((s) => ({ ...s, tier: initialTier }));
  }, [open, initialTier]);

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.company) {
      toast.error("Please fill Name, Phone, Email and Company");
      return;
    }
    const tierMeta = TIER_META[form.tier] || TIER_META.silver;
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company || "",
        gstin: "",
        message: `Managed Services enquiry — ${tierMeta.label} · ~${form.num_users || 0} users.${form.message ? "\n" + form.message : ""}`,
        product_id: null,
        product_name: `Managed Services · ${tierMeta.label}`,
        product_sku: `MSVC-${form.tier.toUpperCase()}`,
        license_type: form.tier.toUpperCase(),
        quantity: Number(form.num_users) || 1,
      });
      trackLead({ source: `managed-tier:${form.tier}`, productSku: `MSVC-${form.tier.toUpperCase()}` });
      toast.success("Enquiry sent — our team will reach out shortly.");
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", company: "", num_users: 10, tier: initialTier, message: "" });
    } catch (err) {
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const tier = TIER_META[form.tier] || TIER_META.silver;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="managed-tier-enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Headset className="h-5 w-5 text-brand-blue" />
            Managed Services Enquiry
          </DialogTitle>
          <DialogDescription>
            You selected <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest ${tier.accent}`} data-testid="mt-selected-tier">{tier.label}</span>
            <span className="ml-2 text-xs text-muted-foreground">· {tier.subtitle}</span>
            <span className="mt-1 block text-sm">Fill in your details — our team will reach out to understand your exact requirements.</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid gap-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mt-name">Full Name *</Label>
              <Input id="mt-name" data-testid="mt-name" value={form.name} onChange={upd("name")} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mt-phone">Phone *</Label>
              <Input id="mt-phone" data-testid="mt-phone" value={form.phone} onChange={upd("phone")} required placeholder="+91 …" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mt-email">Email *</Label>
              <Input id="mt-email" type="email" data-testid="mt-email" value={form.email} onChange={upd("email")} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mt-company">Company <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
              <Input id="mt-company" data-testid="mt-company" value={form.company} onChange={upd("company")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mt-users">Number of Users <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
              <Input id="mt-users" type="number" min="1" data-testid="mt-users" value={form.num_users} onChange={upd("num_users")} />
            </div>
            <div className="space-y-2">
              <Label>Support Tier *</Label>
              <Select value={form.tier} onValueChange={(v) => setForm((s) => ({ ...s, tier: v }))}>
                <SelectTrigger data-testid="mt-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="silver">Silver Support · 1–20 staff</SelectItem>
                  <SelectItem value="gold">Gold Support · 1–100 staff</SelectItem>
                  <SelectItem value="diamond">Diamond Support · Dedicated IT expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mt-msg">Requirements <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
            <Textarea id="mt-msg" rows={3} data-testid="mt-message" value={form.message} onChange={upd("message")} placeholder="Anything specific — office locations, timelines, existing tools, etc." />
          </div>
          <DialogFooter>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between w-full gap-3">
              <p className="text-[10px] font-mono text-muted-foreground">We respond within 1 business day.</p>
              <Button type="submit" data-testid="mt-submit" disabled={submitting} className="bg-brand-blue hover:bg-brand-blueDark rounded-sm">
                {submitting ? "Sending…" : "Send Enquiry"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
